// TasksMenu.jsx
// Place this file in: src/pages/TasksMenu.jsx

import { useState } from "react";
import {
  Table, Tabs, Button, Input, Select, Modal, Form,
  Space, Typography, ConfigProvider, Badge, Tooltip,
} from "antd";
import {
  SearchOutlined, FilterOutlined, ArrowRightOutlined, RightOutlined,
  HistoryOutlined, CopyOutlined, EditOutlined, DeleteOutlined,
} from "@ant-design/icons";
import TaskTypeStatus from "./TaskTypeStatus"; // see TaskTypeStatus.jsx
import CreateTaskModal from "organisms/createTaskModal";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";

const { Title, Text } = Typography;
const { Option } = Select;

// ─── Shared theme ─────────────────────────────────────────────────────────────
const theme = {
  token: {
    colorPrimary: "#10b981",
    borderRadius: 20,
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  },
  components: {
    Table: {
      headerBg: "#fafafa",
      headerColor: "#888",
      headerSplitColor: "transparent",
      rowHoverBg: "#f6fffe",
      fontSize: 13,
    },
    Tabs: {
      inkBarColor: "#111111",
      itemSelectedColor: "#111111",
      itemColor: "#aaaaaa",
      itemHoverColor: "#555555",
      titleFontSize: 14,
      titleFontSizeLG: 14,
    },
    Button: { defaultBorderColor: "#e0e0e0", borderRadius: 8 },
    Select: { fontSize: 13 },
    Input: { fontSize: 13 },
  },
};

const greenBtn = {
  background: "#6ee7b7",
  color: "#065f46",
  fontWeight: 700,
  border: "none",
  boxShadow: "none",
};

const pillSelect = { borderRadius: 20 };

// ─── Initial data ─────────────────────────────────────────────────────────────
const initialTasks = [
  { id: 1, name: "Advisory Task", createdBy: "James", createdDate: "21st Mar, 2024", assignedTo: "William", priority: "High", status: "Open", type: "Other", matter: "Advisory matter", dueDate: "2nd Aug 2026; 10:00 AM", estimatedTime: "1 hr", description: "Prepare and finalize a legal notice on behalf of the client regarding breach of contract dated 12 January 2026. Review the agreement terms, identify specific clauses violated, quantify damages where applicable, and ensure compliance with jurisdictional requirements. Share draft with the senior associate for review before sending it to the client for approval." },
  { id: 2, name: "Advisory Task", createdBy: "James", createdDate: "21st Mar, 2024", assignedTo: "William", priority: "Low", status: "In Progress", type: "Followup", matter: "Advisory matter", dueDate: "5th Sep 2026; 09:00 AM", estimatedTime: "2 hr", description: "Follow up with client on pending documents." },
  { id: 3, name: "Advisory Task", createdBy: "James", createdDate: "21st Mar, 2024", assignedTo: "William", priority: "High", status: "Open", type: "Other", matter: "Advisory matter", dueDate: "10th Oct 2026; 11:00 AM", estimatedTime: "3 hr", description: "Review advisory matter documentation." },
  { id: 4, name: "Advisory Task", createdBy: "James", createdDate: "21st Mar, 2024", assignedTo: "William", priority: "Low", status: "In Progress", type: "Followup", matter: "Advisory matter", dueDate: "15th Nov 2026; 02:00 PM", estimatedTime: "1.5 hr", description: "Coordinate with opposing counsel." },
  { id: 5, name: "Review Task", createdBy: "Alice", createdDate: "22nd Mar, 2024", assignedTo: "James", priority: "Medium", status: "Completed", type: "Review", matter: "Advisory matter", dueDate: "20th Dec 2026; 03:00 PM", estimatedTime: "2 hr", description: "Final review of all submitted documents." },
];

// Mock history per task
const taskHistory = [
  { action: "Edited by", user: "Joseph", bold: true, datetime: "25th Aug 2025; 09:10 AM" },
  { action: "Edited by", user: "You", bold: false, datetime: "25th Aug 2025; 09:10 AM" },
  { action: "Status Updated by", user: "Joseph", bold: true, datetime: "25th Aug 2025; 09:10 AM" },
  { action: "Task Created by", user: "You", bold: false, datetime: "25th Aug 2025; 09:10 AM" },
];

// ─── Icon button used in row hover actions ────────────────────────────────────
function IconBtn({ icon, tooltip, color = "#000", onClick }) {
  return (
    <Tooltip title={tooltip}>
      <div
        onClick={e => { e.stopPropagation(); onClick && onClick(); }}
        style={{
          width: 30, height: 30, borderRadius: "50%",
          background: "#C2FAE5",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color, fontSize: 14,
          transition: "background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#d1fae5"}
        onMouseLeave={e => e.currentTarget.style.background = "#ecfdf5"}
      >
        {icon}
      </div>
    </Tooltip>
  );
}

// ─── Update Status Modal ──────────────────────────────────────────────────────
function UpdateStatusModal({ open, onClose, currentStatus, onUpdate }) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || "Open");

  // Sync when modal opens with a new task
  useState(() => {
    setSelectedStatus(currentStatus || "Open");
  }, [currentStatus, open]);

  const handleUpdate = () => {
    onUpdate(selectedStatus);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Update Status</span>}
      width={420}
      centered
    >
      <div style={{ padding: "8px 0 20px" }}>
        <Select
          value={selectedStatus}
          onChange={setSelectedStatus}
          style={{ width: "100%", borderRadius: 8 }}
          size="large"
        >
          <Option value="Open">Open</Option>
          <Option value="In Progress">In Progress</Option>
          <Option value="Completed">Completed</Option>
        </Select>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <ButtonOutlined
          onClick={onClose}
          style={{ borderColor: "#e0e0e0", borderRadius: 8, minWidth: 90 }}
        >
          Cancel
        </ButtonOutlined>
        <ButtonFilled
          onClick={handleUpdate}
        >
          Update
        </ButtonFilled>
      </div>
    </Modal>
  );
}

// ─── Task History Modal ───────────────────────────────────────────────────────
function TaskHistoryModal({ open, onClose }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Task History</span>}
      width={600}
    >
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr",
        background: "#f9fafb", borderRadius: 8,
        padding: "10px 16px", marginBottom: 4,
        fontWeight: 600, fontSize: 13, color: "#555",
        border: "1px solid #f0f0f0",
      }}>
        <span>Action by</span>
        <span>Date &amp; Time</span>
      </div>
      {taskHistory.map((h, i) => (
        <div key={i} style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          padding: "13px 16px",
          borderBottom: i < taskHistory.length - 1 ? "1px solid #f5f5f5" : "none",
          fontSize: 13, color: "#444",
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderTop: "none",
          borderRadius: i === taskHistory.length - 1 ? "0 0 8px 8px" : 0,
        }}>
          <span>
            {h.action}{" "}
            <strong style={{ color: "#111" }}>{h.user}</strong>
          </span>
          <span style={{ color: "#666" }}>{h.datetime}</span>
        </div>
      ))}
    </Modal>
  );
}

// ─── Task Details Modal ───────────────────────────────────────────────────────
// Renders action icons conditionally based on status, same as table rows.
// Supports Edit, Clone (Duplicate), Delete (Open) | History, Clone, Update Status (In Progress)
function TaskDetailsModal({ open, onClose, task, onEdit, onClone, onDelete, onUpdateStatus }) {
  if (!task) return null;

  const isOpen = task.status === "Open";
  const isInProgress = task.status === "In Progress";
  const isCompleted = task.status === "Completed";

  const fields = [
    { label: "Name of the Task", value: task.name },
    { label: "Priority", value: task.priority },
    { label: "Task Status", value: task.status },
    { label: "Created by", value: `${task.createdBy}; ${task.createdDate}` },
    { label: "Assigned to", value: task.assignedTo },
    { label: "Due Date & Time", value: task.dueDate },
    { label: "Task Type", value: task.type },
    { label: "Matter", value: task.matter },
    { label: "Estimated Time of Completion", value: task.estimatedTime },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 32 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Task Details</span>
          <Space size={8}>
            {/* Open: Clone, Edit, Delete */}
            {isOpen && (
              <>
                <IconBtn icon={<CopyOutlined />} tooltip="Clone" onClick={() => { onClose(); onClone(task); }} />
                <IconBtn icon={<EditOutlined />} tooltip="Edit" onClick={() => { onClose(); onEdit(task); }} />
                <IconBtn icon={<DeleteOutlined />} tooltip="Delete" color="#ef4444" onClick={() => { onClose(); onDelete(task); }} />
              </>
            )}
            {/* In Progress: History, Clone, Update Status */}
            {isInProgress && (
              <>
                <IconBtn icon={<HistoryOutlined />} tooltip="History" onClick={() => { /* history handled separately if needed */ }} />
                <IconBtn icon={<CopyOutlined />} tooltip="Clone" onClick={() => { onClose(); onClone(task); }} />
                <Tooltip title="Update Status">
                  <ButtonOutlined
                    size="small"
                    onClick={() => { onUpdateStatus(task); }}
                  >
                    Update Status
                  </ButtonOutlined>
                </Tooltip>
              </>
            )}
            {/* Completed: History, Clone only */}
            {isCompleted && (
              <>
                <IconBtn icon={<HistoryOutlined />} tooltip="History" onClick={() => { }} />
                <IconBtn icon={<CopyOutlined />} tooltip="Clone" onClick={() => { onClose(); onClone(task); }} />
              </>
            )}
          </Space>
        </div>
      }
    >
      {fields.map((f, i) => (
        <div key={i} style={{
          display: "grid", gridTemplateColumns: "200px 1fr",
          padding: "11px 0",
          borderBottom: i < fields.length - 1 ? "1px solid #f5f5f5" : "none",
          fontSize: 13,
        }}>
          <span style={{ color: "#888" }}>{f.label}</span>
          <span style={{ color: "#111", fontWeight: 500 }}>{f.value}</span>
        </div>
      ))}
      {task.description && (
        <div style={{ paddingTop: 14 }}>
          <div style={{ color: "#888", fontSize: 13, marginBottom: 6 }}>Task Description</div>
          <div style={{ color: "#333", fontSize: 13, lineHeight: 1.65 }}>{task.description}</div>
        </div>
      )}
    </Modal>
  );
}

// ─── Row action icons — conditional by status ─────────────────────────────────
// Open     → History, Clone (Duplicate), Edit, Delete
// InProgress → History, Clone, Update Status button
// Completed  → (no inline actions shown, or minimal)
function RowActions({ record, onHistory, onClone, onEdit, onDelete, onUpdateStatus }) {
  const isOpen = record.status === "Open";
  const isInProgress = record.status === "In Progress";
  const isCompleted = record.status === "Completed";

  return (
    <div
      className="row-actions"
      style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}
      onClick={e => e.stopPropagation()}
    >
      {isOpen && (
        <>
          <IconBtn icon={<HistoryOutlined />} tooltip="History" onClick={() => onHistory(record)} />
          <IconBtn icon={<CopyOutlined />} tooltip="Clone" onClick={() => onClone(record)} />
          <IconBtn icon={<EditOutlined />} tooltip="Edit" onClick={() => onEdit(record)} />
          <IconBtn icon={<DeleteOutlined />} tooltip="Delete" color="#ef4444" onClick={() => onDelete(record)} />
        </>
      )}
      {isInProgress && (
        <>
          <IconBtn icon={<HistoryOutlined />} tooltip="History" onClick={() => onHistory(record)} />
          <IconBtn icon={<CopyOutlined />} tooltip="Clone" onClick={() => onClone(record)} />
          <ButtonFilled
            size="small"
            style={{ borderRadius: 8, height: 30 }}
            onClick={() => onUpdateStatus(record)}
          >
            Update Status
          </ButtonFilled>
        </>
      )}
      {isCompleted && (
        <>
          <IconBtn icon={<HistoryOutlined />} tooltip="History" onClick={() => onHistory(record)} />
          <IconBtn icon={<CopyOutlined />} tooltip="Clone" onClick={() => onClone(record)} />
        </>
      )}
    </div>
  );
}

function buildColumns({ onHistory, onClone, onEdit, onDelete, onUpdateStatus }) {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: text => <Text>{text}</Text>,
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{text}</div>
          <div style={{ color: "#aaa", fontSize: 11, fontStyle: "italic" }}>{record.createdDate}</div>
        </div>
      ),
    },
    { title: "Assigned to", dataIndex: "assignedTo", key: "assignedTo" },
    { title: "Priority", dataIndex: "priority", key: "priority" },
    { title: "Status", dataIndex: "status", key: "status" },
    { title: "Type", dataIndex: "type", key: "type" },
    {
      title: "Matter",
      dataIndex: "matter",
      key: "matter",
      render: text => <Text type="secondary">{text}</Text>,
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <RowActions
          record={record}
          onHistory={onHistory}
          onClone={onClone}
          onEdit={onEdit}
          onDelete={onDelete}
          onUpdateStatus={onUpdateStatus}
        />
      ),
    },
  ];
}

// ─── Filter Toolbar ───────────────────────────────────────────────────────────
function FilterToolbar({ count, label, search, onSearch, statusFilter, onStatus, assigneeFilter, onAssignee, priorityFilter, onPriority }) {
  const activeFilterCount = [statusFilter, assigneeFilter, priorityFilter].filter(Boolean).length;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "16px 0" }}>
      <Text style={{ fontSize: 13.5, color: "#555", whiteSpace: "nowrap", marginRight: 4 }}>
        {label}: <strong style={{ color: "#111" }}>{count}</strong>
      </Text>
      <div style={{ flex: 1 }} />
      <Input
        prefix={<SearchOutlined style={{ color: "#bbb" }} />}
        placeholder="Search" value={search}
        onChange={e => onSearch(e.target.value)}
        style={{ width: 180, borderRadius: 20 }} allowClear
      />
      <Select placeholder={<><FilterOutlined style={{ marginRight: 4 }} />Status</>} allowClear style={{ width: 130, ...pillSelect }} value={statusFilter} onChange={onStatus}>
        <Option value="Open">Open</Option>
        <Option value="In Progress">In Progress</Option>
        <Option value="Completed">Completed</Option>
      </Select>
      <Select placeholder={<><FilterOutlined style={{ marginRight: 4 }} />Assignee</>} allowClear style={{ width: 140, ...pillSelect }} value={assigneeFilter} onChange={onAssignee}>
        <Option value="William">William</Option>
        <Option value="James">James</Option>
        <Option value="Alice">Alice</Option>
      </Select>
      <Select placeholder={<><FilterOutlined style={{ marginRight: 4 }} />Priority</>} allowClear style={{ width: 130, ...pillSelect }} value={priorityFilter} onChange={onPriority}>
        <Option value="High">High</Option>
        <Option value="Medium">Medium</Option>
        <Option value="Low">Low</Option>
      </Select>
      <Badge count={activeFilterCount} size="small" color="#ef4444">
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <FilterOutlined style={{ color: "#065f46", fontSize: 15 }} />
        </div>
      </Badge>
    </div>
  );
}

// ─── Shared task modal state hook ─────────────────────────────────────────────
// Manages create/edit/clone modal state centrally.
// mode: "create" | "edit" | "clone"
function useTaskModalState(tasks, setTasks) {
  const [taskModalMode, setTaskModalMode] = useState(null); // null = closed
  const [taskModalData, setTaskModalData] = useState(null); // pre-fill data

  const openCreate = () => { setTaskModalMode("create"); setTaskModalData(null); };
  const openEdit = (task) => { setTaskModalMode("edit"); setTaskModalData(task); };
  const openClone = (task) => { setTaskModalMode("clone"); setTaskModalData(task); };
  const closeTaskModal = () => { setTaskModalMode(null); setTaskModalData(null); };

  // Called by CreateTaskModal on submit
  const handleTaskModalSubmit = (values) => {
    if (taskModalMode === "edit" && taskModalData) {
      setTasks(prev => prev.map(t =>
        t.id === taskModalData.id ? { ...t, ...values } : t
      ));
    } else {
      // create or clone
      setTasks(prev => [...prev, {
        id: Date.now(),
        name: values.name,
        createdBy: "James",
        createdDate: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        assignedTo: values.assignedTo,
        priority: values.priority,
        status: values.status,
        type: values.type,
        matter: values.matter,
        dueDate: "—",
        estimatedTime: "—",
        description: "",
      }]);
    }
    closeTaskModal();
  };

  return {
    taskModalMode,
    taskModalData,
    openCreate,
    openEdit,
    openClone,
    closeTaskModal,
    handleTaskModalSubmit,
  };
}

// ─── All Tasks Page ───────────────────────────────────────────────────────────
function AllTasksPage({ tasks, setTasks, onBack }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [updateStatusTask, setUpdateStatusTask] = useState(null);

  const {
    taskModalMode, taskModalData,
    openEdit, openClone, closeTaskModal, handleTaskModalSubmit,
  } = useTaskModalState(tasks, setTasks);

  const handleDelete = (record) => {
    setTasks(prev => prev.filter(t => t.id !== record.id));
  };

  const handleUpdateStatus = (status) => {
    if (updateStatusTask) {
      setTasks(prev => prev.map(t =>
        t.id === updateStatusTask.id ? { ...t, status } : t
      ));
      // Refresh detailsTask if open
      setDetailsTask(prev => prev && prev.id === updateStatusTask.id ? { ...prev, status } : prev);
    }
    setUpdateStatusTask(null);
  };

  const displayed = tasks.filter(t => {
    const q = search.toLowerCase();
    return (
      (!q || t.name.toLowerCase().includes(q) || t.assignedTo.toLowerCase().includes(q) || t.matter.toLowerCase().includes(q)) &&
      (!statusFilter || t.status === statusFilter) &&
      (!assigneeFilter || t.assignedTo === assigneeFilter) &&
      (!priorityFilter || t.priority === priorityFilter)
    );
  });

  const columns = buildColumns({
    onHistory: () => setHistoryOpen(true),
    onClone: openClone,
    onEdit: openEdit,
    onDelete: handleDelete,
    onUpdateStatus: (record) => setUpdateStatusTask(record),
  });

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 24, fontSize: 14 }}>
        <span onClick={onBack} style={{ cursor: "pointer", color: "#555", fontWeight: 500 }}>Task</span>
        <RightOutlined style={{ fontSize: 10, color: "#aaa" }} />
        <span style={{ color: "#111", fontWeight: 600 }}>All Tasks</span>
      </div>
      <FilterToolbar
        count={displayed.length} label="Total Tasks"
        search={search} onSearch={setSearch}
        statusFilter={statusFilter} onStatus={setStatusFilter}
        assigneeFilter={assigneeFilter} onAssignee={setAssigneeFilter}
        priorityFilter={priorityFilter} onPriority={setPriorityFilter}
      />
      <style>{`.row-actions { opacity: 0; transition: opacity 0.15s; } .ant-table-row:hover .row-actions { opacity: 1; }`}</style>
      <Table
        dataSource={displayed} columns={columns} rowKey="id"
        pagination={false} size="middle"
        style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}
        locale={{ emptyText: "No tasks found" }}
        scroll={{ x: true }}
        onRow={record => ({ onClick: () => setDetailsTask(record), style: { cursor: "pointer" } })}
      />

      <TaskHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <TaskDetailsModal
        open={!!detailsTask}
        onClose={() => setDetailsTask(null)}
        task={detailsTask}
        onEdit={(task) => { setDetailsTask(null); openEdit(task); }}
        onClone={(task) => { setDetailsTask(null); openClone(task); }}
        onDelete={(task) => { setDetailsTask(null); handleDelete(task); }}
        onUpdateStatus={(task) => { setUpdateStatusTask(task); }}
      />

      <UpdateStatusModal
        open={!!updateStatusTask}
        onClose={() => setUpdateStatusTask(null)}
        currentStatus={updateStatusTask?.status}
        onUpdate={handleUpdateStatus}
      />

      {/* Shared CreateTaskModal for create / edit / clone */}
      <CreateTaskModal
        visible={!!taskModalMode}
        onClose={closeTaskModal}
        onSubmit={handleTaskModalSubmit}
        mode={taskModalMode}
        // Pass pre-population data; CreateTaskModal should use initialValues from this
        initialValues={taskModalData ? {
          name: taskModalMode === "clone" ? `${taskModalData.name} (Copy)` : taskModalData.name,
          assignedTo: taskModalData.assignedTo,
          priority: taskModalData.priority,
          status: taskModalData.status,
          type: taskModalData.type,
          matter: taskModalData.matter,
        } : undefined}
      />
    </div>
  );
}

// ─── Main Tasks Page ──────────────────────────────────────────────────────────
function TasksPage({ onTypeStatusClick, onAllTasksClick, tasks, setTasks }) {
  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [updateStatusTask, setUpdateStatusTask] = useState(null);

  const {
    taskModalMode, taskModalData,
    openCreate, openEdit, openClone, closeTaskModal, handleTaskModalSubmit,
  } = useTaskModalState(tasks, setTasks);

  const handleDelete = (record) => {
    setTasks(prev => prev.filter(t => t.id !== record.id));
  };

  const handleUpdateStatus = (status) => {
    if (updateStatusTask) {
      setTasks(prev => prev.map(t =>
        t.id === updateStatusTask.id ? { ...t, status } : t
      ));
      setDetailsTask(prev => prev && prev.id === updateStatusTask.id ? { ...prev, status } : prev);
    }
    setUpdateStatusTask(null);
  };

  const byTab = tasks.filter(t =>
    activeTab === "pending" ? t.status !== "Completed" : t.status === "Completed"
  );

  const displayed = byTab.filter(t => {
    const q = search.toLowerCase();
    return (
      (!q || t.name.toLowerCase().includes(q) || t.assignedTo.toLowerCase().includes(q) || t.matter.toLowerCase().includes(q)) &&
      (!statusFilter || t.status === statusFilter) &&
      (!assigneeFilter || t.assignedTo === assigneeFilter) &&
      (!priorityFilter || t.priority === priorityFilter)
    );
  });

  const columns = buildColumns({
    onHistory: () => setHistoryOpen(true),
    onClone: openClone,
    onEdit: openEdit,
    onDelete: handleDelete,
    onUpdateStatus: (record) => setUpdateStatusTask(record),
  });

  const tabLabel = activeTab === "pending" ? "Pending" : "Completed";
  const activeFilterCount = [statusFilter, assigneeFilter, priorityFilter].filter(Boolean).length;

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <style>{`
        .row-actions { opacity: 0; transition: opacity 0.15s; }
        .ant-table-row:hover .row-actions { opacity: 1; }
      `}</style>

      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, fontSize: 14 }}>
        <span style={{ color: "#555", fontWeight: 500 }}>Task</span>
      </div>

      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, fontWeight: 700, letterSpacing: "-0.4px" }}>Tasks</Title>
        <Space size={8} wrap>
          <Button type="text" onClick={onAllTasksClick}
            style={{ color: "#444", fontSize: 13, fontWeight: 500 }}>
            All Tasks <ArrowRightOutlined />
          </Button>
          <ButtonOutlined onClick={onTypeStatusClick}>Type &amp; Status</ButtonOutlined>
          <ButtonFilled onClick={openCreate}>Create Task</ButtonFilled>
        </Space>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={key => { setActiveTab(key); setSearch(""); setStatusFilter(null); setAssigneeFilter(null); setPriorityFilter(null); }}
        style={{ marginBottom: 0 }}
        items={[
          {
            key: "pending",
            label: <span style={{ fontWeight: activeTab === "pending" ? 700 : 400, color: activeTab === "pending" ? "#111" : "#aaa", fontSize: 14 }}>Pending</span>,
          },
          {
            key: "completed",
            label: <span style={{ fontWeight: activeTab === "completed" ? 700 : 400, color: activeTab === "completed" ? "#111" : "#aaa", fontSize: 14 }}>Completed</span>,
          },
        ]}
      />

      {/* Count + Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "16px 0" }}>
        <Text style={{ fontSize: 13.5, color: "#555", whiteSpace: "nowrap", marginRight: 4 }}>
          Total {tabLabel}: <strong style={{ color: "#111" }}>{displayed.length}</strong>
        </Text>
        <div style={{ flex: 1 }} />
        <Input
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="Search" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 180, borderRadius: 20 }} allowClear
        />
        <Select placeholder={<><FilterOutlined style={{ marginRight: 4 }} />Status</>} allowClear style={{ width: 130, ...pillSelect }} value={statusFilter} onChange={setStatusFilter}>
          <Option value="Open">Open</Option>
          <Option value="In Progress">In Progress</Option>
          <Option value="Completed">Completed</Option>
        </Select>
        <Select placeholder={<><FilterOutlined style={{ marginRight: 4 }} />Assignee</>} allowClear style={{ width: 140, ...pillSelect }} value={assigneeFilter} onChange={setAssigneeFilter}>
          <Option value="William">William</Option>
          <Option value="James">James</Option>
          <Option value="Alice">Alice</Option>
        </Select>
        <Select placeholder={<><FilterOutlined style={{ marginRight: 4 }} />Priority</>} allowClear style={{ width: 130, ...pillSelect }} value={priorityFilter} onChange={setPriorityFilter}>
          <Option value="High">High</Option>
          <Option value="Medium">Medium</Option>
          <Option value="Low">Low</Option>
        </Select>
        <Badge count={activeFilterCount} size="small" color="#ef4444">
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <FilterOutlined style={{ color: "#065f46", fontSize: 15 }} />
          </div>
        </Badge>
      </div>

      {/* Table */}
      <Table
        dataSource={displayed} columns={columns} rowKey="id"
        pagination={false} size="middle"
        style={{ borderRadius: 10, border: "1px solid #f0f0f0", overflow: "hidden" }}
        locale={{ emptyText: "No tasks found" }}
        scroll={{ x: true }}
        onRow={record => ({ onClick: () => setDetailsTask(record), style: { cursor: "pointer" } })}
      />

      {/* Task History Modal */}
      <TaskHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />

      {/* Task Details Modal */}
      <TaskDetailsModal
        open={!!detailsTask}
        onClose={() => setDetailsTask(null)}
        task={detailsTask}
        onEdit={(task) => { setDetailsTask(null); openEdit(task); }}
        onClone={(task) => { setDetailsTask(null); openClone(task); }}
        onDelete={(task) => { setDetailsTask(null); handleDelete(task); }}
        onUpdateStatus={(task) => { setUpdateStatusTask(task); }}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        open={!!updateStatusTask}
        onClose={() => setUpdateStatusTask(null)}
        currentStatus={updateStatusTask?.status}
        onUpdate={handleUpdateStatus}
      />

      {/* Unified CreateTaskModal — handles create / edit / clone */}
      <CreateTaskModal
        visible={!!taskModalMode}
        onClose={closeTaskModal}
        onSubmit={handleTaskModalSubmit}
        mode={taskModalMode}
        initialValues={taskModalData ? {
          name: taskModalMode === "clone" ? `${taskModalData.name} (Copy)` : taskModalData.name,
          assignedTo: taskModalData.assignedTo,
          priority: taskModalData.priority,
          status: taskModalData.status,
          type: taskModalData.type,
          matter: taskModalData.matter,
        } : undefined}
      />
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function TasksMenu() {
  const [page, setPage] = useState("tasks");
  const [tasks, setTasks] = useState(initialTasks);

  return (
    <ConfigProvider theme={theme}>
      {page === "tasks" && (
        <TasksPage
          tasks={tasks}
          setTasks={setTasks}
          onAllTasksClick={() => setPage("allTasks")}
          onTypeStatusClick={() => setPage("typeStatus")}
        />
      )}
      {page === "allTasks" && (
        <AllTasksPage
          tasks={tasks}
          setTasks={setTasks}
          onBack={() => setPage("tasks")}
        />
      )}
      {page === "typeStatus" && (
        <TaskTypeStatus onBack={() => setPage("tasks")} />
      )}
    </ConfigProvider>
  );
}